import React, { useState } from "react";
import { ArrowLeft, Info, User } from "lucide-react";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import QuickActions from "./quickAction";
import { UploadReadingModal } from "./uploadReadingModal";
import { UpdatePremiseModal } from "./updatePremiseModal";
import { CreateServiceOrderModal } from "./serviceOrderModal";
import { UpdateConsumerModal } from "./updateConsumerModal";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";

const HeaderDetails = ({ meter }) => {
  const navigate = useNavigate();
  const [isUploadReadingOpen, setIsUploadReadingOpen] = useState(false);
  const [isUpdatePremiseOpen, setIsUpdatePremiseOpen] = useState(false);
  const [isCreateServiceOrderOpen, setIsCreateServiceOrderOpen] =
    useState(false);
  const [isUpdateConsumerOpen, setIsUpdateConsumerOpen] = useState(false);

  const meterDetails = [
    {
      label: "Meter Number",
      value: meter?.meterNumber,
      tooltip:
        "Assigned by utility at installation. Used for all operations and reporting.",
    },
    {
      label: "Device Number",
      value: meter?.deviceNo,
      tooltip:
        "Unique hardware identifier assigned by the manufacturer. Used for technical support and device tracking.",
    },
    {
      label: "Meter Type",
      value: meter?.meterTypeDisplay,
      tooltip:
        "Determines how readings are collected — manual entry, photo upload, or automated transmission.",
    },
    {
      label: "Utility Service",
      value: meter?.utilityService,
      tooltip:
        "Type of utility being measured (water, electricity, gas). Determines billing rates and consumption units.",
    },
    {
      label: "Reading Status",
      value: meter?.currentReadingStatus,
      tooltip:
        "Shows most recent validation result for readings. Affects billing accuracy.",
    },
    {
      label: "Manufacturer",
      value: meter?.meterModel?.meterMake,
      tooltip:
        "Company that produced this meter. Important for warranty claims, spare parts, and technical support.",
    },
    {
      label: "Meter Model",
      value: meter?.meterModel?.meterModel,
      tooltip:
        "Specific model designation from the manufacturer. Determines technical capabilities and compatibility.",
    },
    {
      label: "Meter Size",
      value: meter?.meterModel?.technicalSpecifications?.connectionSize,
      tooltip:
        "Physical connection diameter. Must match pipe size for proper installation and accurate measurement.",
    },
    {
      label: "Category",
      value: meter?.categoryDisplay,
      tooltip:
        "Consumer classification (e.g., Residential, Commercial, Industrial). Affects tariff rates and billing cycles.",
    },
    {
      label: "Sub Category",
      value: meter?.subCategoryDisplay,
      tooltip:
        "Detailed consumer type (e.g., Single Family, Retail, Manufacturing). Used for granular billing and analytics.",
    },
  ];

  const installationDetails = [
    {
      label: "Installation Date",
      value: meter?.installedOn,
      tooltip:
        "Date when the meter was physically installed at this location. Used to track warranty and maintenance schedules.",
    },
    {
      label: "Age",
      value: meter?.ageDisplay,
      tooltip:
        "Time elapsed since installation. Helps identify meters approaching end-of-life or requiring replacement.",
    },
    { label: "Premise", value: meter?.premiseName, tooltip: "" },
    {
      label: "Area / Subarea",
      value: meter?.areaName,
      tooltip:
        "Geographic zone and subdivision for operational planning, route optimization, and regional reporting.",
    },
  ];

  const readingDetails = [
    {
      label: "Last Reading",
      value: (
        <div>
          {meter?.lastReading && meter?.lastReadingDate ? (
            <>
              <div>
                {meter?.lastReading} {meter?.utilityServiceUnit}
              </div>
              <div className="text-sm text-muted-foreground">
                on {meter?.lastReadingDate}
              </div>
            </>
          ) : (
            <div>NA</div>
          )}
        </div>
      ),
      tooltip: "Last reading recorded and validated by the system.",
    },
    {
      label: "Days Since Last Reading",
      value: meter?.daysSinceLastReading,
      tooltip: "",
    },
    {
      label: "Avg. Consumption",
      value: meter?.monthlyAverageConsumption,
      tooltip:
        "Average monthly consumption from the past 12 months of validated readings.",
    },
  ];

  const operationalDetails = [
    {
      label: "Route",
      value: meter?.route,
      tooltip: "Designated reading path for field workers.",
    },
    {
      label: "Read Cycle",
      value: meter?.meterCycle,
      tooltip: "Billing period identifier for reading and billing cycles.",
    },
    {
      label: "Next Reading Expected",
      value: meter?.nextReadingDate,
      tooltip: "Next scheduled reading period for this meter.",
    },
  ];

  return (
    <div className="border-b bg-card">
      <div className="pb-4">
        {/* Back Button */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Meters
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Meter Details Card */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <User className="w-16 h-16 rounded-lg border p-3 text-gray-500 bg-gray-100" />
                  <div className="w-full flex justify-between items-start gap-2">
                    <div>
                      <CardTitle className="text-2xl font-bold tracking-tight">
                        {meter?.meterNumber}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground">
                        Device: {meter?.deviceNo}
                      </CardDescription>
                    </div>

                    <div className="flex items-center gap-2 pt-2 flex-nowrap">
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                        Physical: {meter?.consumerStatus || "NA"}
                      </Badge>
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        Assignment: {meter?.status}
                      </Badge>
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                        Reading: {meter?.currentReadingStatus || "NA"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <Card className="mt-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Meter Overview
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-4 mt-3">
                  {meterDetails.map((item) => (
                    <ValueLabel
                      key={item.label}
                      label={item.label}
                      value={item.value}
                      tooltip={item.tooltip}
                    />
                  ))}
                </div>
              </Card>
              <Card className="mt-2">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Installation & Premise Details
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-4 mt-3">
                  {installationDetails.map((item) => (
                    <ValueLabel
                      key={item.label}
                      label={item.label}
                      value={item.value}
                      tooltip={item.tooltip}
                    />
                  ))}
                </div>
              </Card>
              <Card className="mt-2">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Reading & Consumption
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-4 mt-3">
                  {readingDetails.map((item) => (
                    <ValueLabel
                      key={item.label}
                      label={item.label}
                      value={item.value}
                      tooltip={item.tooltip}
                    />
                  ))}
                </div>
              </Card>
            </Card>
          </div>

          {/* Quick Actions */}
          <div>
            <div className="rounded-xl border border-gray-200 p-5 mb-2">
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                <CardTitle className="text-lg font-semibold">
                  Operational Assignment
                </CardTitle>
              </div>

              <div className="space-y-2 mt-3">
                {operationalDetails.map((item) => (
                  <div
                    key={item.label}
                    className="flex justify-between text-sm mt-2"
                  >
                    <span className="text-gray-700">{item.label}</span>
                    <span className="font-semibold text-gray-900">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <QuickActions
                meter={meter}
                onEditMeter={() =>
                  navigate(`/mx/device-management/meter/edit/${meter?.id}`)
                }
                onUpdatePremise={() => setIsUpdatePremiseOpen(true)}
                onUploadReading={() => setIsUploadReadingOpen(true)}
                onGenerateServiceOrder={() => setIsCreateServiceOrderOpen(true)}
                onUpdateConsumer={() => setIsUpdateConsumerOpen(true)}
              />
            </div>

            {/* Modals */}
            <UploadReadingModal
              isOpen={isUploadReadingOpen}
              onClose={() => setIsUploadReadingOpen(false)}
              meter={meter}
            />

            <UpdatePremiseModal
              open={isUpdatePremiseOpen}
              onOpenChange={setIsUpdatePremiseOpen}
              meter={meter}
            />

            <CreateServiceOrderModal
              isOpen={isCreateServiceOrderOpen}
              onClose={() => setIsCreateServiceOrderOpen(false)}
            />

            <UpdateConsumerModal
              open={isUpdateConsumerOpen}
              onOpenChange={setIsUpdateConsumerOpen}
              meter={meter}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderDetails;
